<template>
  <div class="shell-page purchase-status-page client-account-page">
    <HeaderMain v-if="!currentSession" hide-wallet @messageFromHeader="searchEvents" />
    <SearchCourse v-if="!main" :courses="search" />

    <section class="purchase-status-section client-account-section">
      <TicketAccessGate
        :prefill-email="$route.query.email || ''"
        title="Entra a tu cuenta FOMO"
        subtitle="Valida tu correo una sola vez para ver tus entradas, vender con Swap, perfil y soporte."
        @authenticated="loadAccount"
        @signed-out="resetAccount"
      >
        <template #default="{ session, logout }">
          <div class="client-account-workspace workspace-v2 workspace-v2--event">
            <OrganizerWorkspaceTopbar :menu-open="showNav && isMobileViewport" menu-label="Abrir menú de cuenta" :brand-route="{ name: 'usertickets' }" @open-menu="showNav = true" />

            <OrganizerWorkspaceNav
              class="workspace-v2__nav--mobile"
              :mobile-open="showNav && isMobileViewport"
              :drawer-mode="true"
              :brand-route="{ name: 'usertickets' }"
              workspace-eyebrow="Cuenta FOMO"
              :workspace-items="clientWorkspaceNavItems"
              context-eyebrow="FOMO Swap"
              :context-label="clientSwapContextLabel"
              :context-items="clientSwapContextNavItems"
              :identity-label-override="session.email"
              :logout-route="null"
              logout-label="Cambiar correo"
              @logout="logout"
              @close="showNav = false"
            />

            <div class="workspace-v2__shell client-account-shell">
              <OrganizerWorkspaceNav
                class="workspace-v2__nav--desktop"
                :mobile-open="showNav"
                :brand-route="{ name: 'usertickets' }"
                workspace-eyebrow="Cuenta FOMO"
                :workspace-items="clientWorkspaceNavItems"
                context-eyebrow="FOMO Swap"
                :context-label="clientSwapContextLabel"
                :context-items="clientSwapContextNavItems"
                :identity-label-override="session.email"
                :logout-route="null"
                logout-label="Cambiar correo"
                @logout="logout"
                @close="showNav = false"
              />

              <main class="workspace-v2__main client-account-main">
                <article v-if="activeSection !== 'sell'" class="glass-surface workspace-panel client-account-hero">
                  <div class="workspace-panel__header">
                    <div>
                      <span class="workspace-kicker">Cuenta FOMO</span>
                      <h2 class="workspace-title">{{ activeSection === 'sell' ? 'Vender Entradas' : activeSection === 'entries' ? 'Mis Entradas' : activeMenuLabel }}</h2>
                      <p class="workspace-copy">Compra, reventa protegida, perfil y soporte desde una sola cuenta cliente.</p>
                    </div>
                  </div>

                  <div class="workspace-metrics client-account-metrics">
                    <div class="workspace-metric">
                      <small>Accesos</small>
                      <strong>{{ ticketCount }}</strong>
                    </div>
                    <div class="workspace-metric">
                      <small>Eventos</small>
                      <strong>{{ entryEventGroups.length }}</strong>
                    </div>
                    <div class="workspace-metric">
                      <small>Swap</small>
                      <strong>{{ swapOrders.length }}</strong>
                    </div>
                    <div class="workspace-metric client-account-metric--email">
                      <small>Cuenta</small>
                      <strong>{{ session.email }}</strong>
                    </div>
                  </div>
                </article>

                <div v-if="loadingAccount" class="glass-surface workspace-panel workspace-empty">Cargando tu cuenta...</div>
                <div v-else-if="accountError" class="glass-surface workspace-panel workspace-feedback workspace-feedback--error">{{ accountError }}</div>

                <template v-else>
                  <section v-if="activeSection === 'entries'" class="workspace-stack">
                    <article class="glass-surface workspace-panel">
                      <div class="workspace-panel__header">
                        <div>
                          <span class="workspace-kicker">Mis Entradas</span>
                          <h3 class="workspace-title">Eventos y accesos</h3>
                          <p class="workspace-copy">Original FOMO y compras Swap agrupadas por evento.</p>
                        </div>
                        <router-link class="workspace-button workspace-button--small client-account-link" :to="{ name: 'home' }">Explorar eventos</router-link>
                      </div>

                      <div v-if="!entryEventGroups.length" class="workspace-empty client-account-empty-state">
                        <strong>Todavía no tienes entradas activas.</strong>
                        <span>Cuando compres o recibas una entrada Swap aparecerá aquí.</span>
                        <router-link class="workspace-button workspace-button--primary client-account-link" :to="{ name: 'home' }">Explorar eventos</router-link>
                      </div>

                      <div v-else class="purchase-ticket-list client-entry-list">
                        <article v-for="group in entryEventGroups" :key="group.key" class="purchase-ticket-card client-entry-card">
                          <div class="client-entry-card__banner" :style="eventBannerStyle(group)">
                            <div class="client-entry-card__badges">
                              <span v-for="badge in group.badges" :key="badge" :class="['purchase-ticket-card__status', sourceBadgeClass(badge)]">{{ badge }}</span>
                            </div>
                            <span class="purchase-ticket-card__eyebrow">{{ formatDateShort(group.event_date) }}</span>
                          </div>

                          <div class="purchase-ticket-card__hero client-entry-card__hero">
                            <div>
                              <h3>{{ group.event_name }}</h3>
                              <p>{{ group.venue_name || 'Venue por confirmar' }}<span v-if="group.city_name"> - {{ group.city_name }}</span></p>
                            </div>
                            <div class="purchase-ticket-card__hero-meta">
                              <strong>{{ formatMoney(group.total, group.currency) }}</strong>
                              <small>{{ group.ticket_count }} acceso{{ group.ticket_count === 1 ? '' : 's' }}</small>
                            </div>
                          </div>

                          <div class="purchase-ticket-card__details-grid">
                            <div class="purchase-ticket-card__detail">
                              <span>Órdenes</span>
                              <strong>{{ group.orders.length }}</strong>
                            </div>
                            <div class="purchase-ticket-card__detail">
                              <span>Origen</span>
                              <strong>{{ group.badges.join(' + ') }}</strong>
                            </div>
                            <div class="purchase-ticket-card__detail">
                              <span>Estado</span>
                              <strong>{{ group.status_label }}</strong>
                            </div>
                          </div>

                          <div class="purchase-ticket-card__actions">
                            <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="toggleEventGroup(group)">{{ expandedEventKey === group.key ? 'Ocultar detalle' : 'Abrir entradas' }}</button>
                            <router-link v-if="group.event_slug" class="workspace-button workspace-button--small client-account-link" :to="{ name: 'event', params: { slug: group.event_slug } }">Ver evento</router-link>
                          </div>

                          <div v-if="expandedEventKey === group.key" class="purchase-ticket-card__items client-entry-card__details">
                            <div v-for="order in group.orders" :key="orderKey(order)" class="purchase-ticket-card__item">
                              <div class="purchase-ticket-card__item-main">
                                <strong>{{ order.source === 'swap' ? `Swap ${order.order_number}` : `Orden #${order.display_id || order.invoice_id}` }}</strong>
                                <small>{{ itemCount(order) }} ticket{{ itemCount(order) === 1 ? '' : 's' }} · {{ orderStatusLabel(order) }}</small>
                              </div>
                              <div class="purchase-ticket-card__item-side">
                                <a v-if="order.ticket_ready && order.ticket_file_url" :href="order.ticket_file_url" target="_blank" rel="noreferrer">Descargar</a>
                                <button v-if="order.source === 'swap'" class="workspace-button workspace-button--small" type="button" @click="toggleSwapOrder(order)">{{ expandedSwapOrderNumber === order.order_number ? 'Cerrar Swap' : 'Gestionar Swap' }}</button>
                              </div>
                            </div>

                            <div v-for="order in group.orders.filter((item) => item.source === 'swap' && expandedSwapOrderNumber === item.order_number)" :key="`swap-detail-${order.order_number}`" class="client-account-swap-detail">
<SwapOrderReceipt v-if="swapOrderDetails[order.order_number]?.raw" :order="swapOrderDetails[order.order_number].raw" @updated="updateSwapReceipt" />
                              <p v-else role="status">Cargando tu compra…</p>
                            </div>
                          </div>
                        </article>
                      </div>
                    </article>
                  </section>

                  <section v-else-if="activeSection === 'sell'" class="workspace-stack">
                    <SellerSwapWorkspaceView embedded auth-kind="ticket" :show-embedded-menu="false" :section="$route.query.sellSection || 'sell'" />
                  </section>

                  <section v-else-if="activeSection === 'profile'" class="glass-surface workspace-panel">
                <div class="workspace-panel__header">
                  <div>
                    <span class="workspace-kicker">Perfil</span>
                    <h3 class="workspace-title">Datos de cuenta</h3>
                    <p class="workspace-copy">Estos datos alimentan tus entradas y tu perfil vendedor Swap.</p>
                  </div>
                </div>
                <div class="workspace-form workspace-form--two">
                  <label class="workspace-field"><span>Nombres</span><input class="workspace-input" :value="session.first_name || ''" readonly></label>
                  <label class="workspace-field"><span>Apellidos</span><input class="workspace-input" :value="session.last_name || ''" readonly></label>
                  <label class="workspace-field workspace-field--wide"><span>Correo</span><input class="workspace-input" :value="session.email" readonly></label>
                </div>
              </section>

                  <section v-else class="glass-surface workspace-panel">
                <div class="workspace-panel__header">
                  <div>
                    <span class="workspace-kicker">Soporte</span>
                    <h3 class="workspace-title">Ayuda con tus accesos</h3>
                    <p class="workspace-copy">Para entradas normales revisa el evento. Para compras Swap abre el detalle desde Mis Entradas y usa entrega, mensajes o reclamo.</p>
                  </div>
                </div>
                <a class="workspace-button workspace-button--primary client-account-link" href="mailto:fomoplanes@sudenec.com">Contactar soporte</a>
                  </section>
                </template>
              </main>
            </div>
          </div>
        </template>
      </TicketAccessGate>
    </section>

    <FooterMain />
  </div>
</template>

<script>
import { apiServiceAuth } from '@/common/api.services'
import FooterMain from '@/components/FooterMain.vue'
import HeaderMain from '@/components/HeaderMain.vue'
import SearchCourse from '@/components/SearchCourse.vue'
import TicketAccessGate from '@/components/TicketAccessGate.vue'
import OrganizerWorkspaceNav from '@/components/workspace/OrganizerWorkspaceNav.vue'
import OrganizerWorkspaceTopbar from '@/components/workspace/OrganizerWorkspaceTopbar.vue'
import SwapOrderReceipt from '@/components/swap/SwapOrderReceipt.vue'
import SellerSwapWorkspaceView from '@/pages/host/SellerSwapWorkspaceView.vue'

export default {
  name: 'UserTicketView',

  components: {
    SwapOrderReceipt,
    FooterMain,
    HeaderMain,
    OrganizerWorkspaceNav,
    OrganizerWorkspaceTopbar,
    SearchCourse,
    SellerSwapWorkspaceView,
    TicketAccessGate,
  },

  data() {
    return {
      search: [],
      searchField: '',
      main: true,
      showNav: false,
      isMobileViewport: typeof window !== 'undefined' ? window.matchMedia('(max-width: 980px)').matches : false,
      activeSection: this.normalizeSection(this.$route.query.tab),
      loadingAccount: false,
      accountError: '',
      orders: [],
      swapOrders: [],
      ticketCount: 0,
      currentSession: null,
      expandedEventKey: '',
      expandedSwapOrderNumber: '',
      swapOrderDetails: {},
      swapArtifacts: {},
      swapMessages: {},
      loadingSwapKey: '',
      loadingArtifactsKey: '',
      loadingMessagesKey: '',
      menuItems: [
        { key: 'entries', label: 'Mis Entradas', icon: 'fas fa-ticket-alt' },
        { key: 'sell', label: 'Vender Entradas', icon: 'fas fa-right-left' },
        { key: 'profile', label: 'Perfil', icon: 'fas fa-user' },
        { key: 'support', label: 'Soporte', icon: 'fas fa-life-ring' },
      ],
    }
  },

  computed: {
    activeMenuLabel() {
      const active = this.menuItems.find((item) => item.key === this.activeSection)
      return active ? active.label : 'Mis Entradas'
    },
    clientWorkspaceNavItems() {
      const baseQuery = { ...this.$route.query }
      delete baseQuery.sellSection
      const swapContextOwnsActive = this.showSwapContextNav && ['entries', 'sell'].includes(this.activeSection)
      return this.menuItems.map((item) => ({
        label: item.label,
        icon: item.icon,
        to: {
          name: 'usertickets',
          query: item.key === 'sell' && this.$route.query.sellSection
            ? { ...baseQuery, tab: item.key, sellSection: this.normalizeSwapSection(this.$route.query.sellSection) }
            : { ...baseQuery, tab: item.key },
        },
        active: this.activeSection === item.key && !swapContextOwnsActive,
      }))
    },
    entryEventGroups() {
      return this.groupOrdersByEvent([...(this.orders || []), ...(this.swapOrders || [])])
    },
    expandedEventGroup() {
      return this.entryEventGroups.find((group) => group.key === this.expandedEventKey) || null
    },
    expandedEventHasSwap() {
      return Boolean(this.expandedEventGroup && this.expandedEventGroup.orders.some((order) => order.source === 'swap'))
    },
    showSwapContextNav() {
      return this.activeSection === 'sell' || this.expandedEventHasSwap
    },
    clientActiveSwapSection() {
      if (this.activeSection === 'sell') return this.normalizeSwapSection(this.$route.query.sellSection)
      if (this.expandedEventHasSwap) return 'tickets'
      return ''
    },
    clientSwapContextLabel() {
      if (this.activeSection === 'entries' && this.expandedEventGroup && this.expandedEventGroup.event_name) return this.expandedEventGroup.event_name
      return 'Vender y gestionar'
    },
    clientSwapContextNavItems() {
      if (!this.showSwapContextNav) return []
      return [
        { key: 'sell', label: 'Vender', icon: 'fas fa-plus-circle' },
        { key: 'tickets', label: 'Mis ventas', icon: 'fas fa-ticket' },
        { key: 'finance', label: 'Cobros', icon: 'fas fa-wallet' },
        { key: 'profile', label: 'Perfil Swap', icon: 'fas fa-user-shield' },
      ].map((item) => ({
        label: item.label,
        icon: item.icon,
        to: { name: 'usertickets', query: { ...this.$route.query, tab: 'sell', sellSection: item.key } },
        active: this.clientActiveSwapSection === item.key,
      }))
    },
  },

  watch: {
    '$route.query.tab'(value) {
      this.activeSection = this.normalizeSection(value)
    },
  },

  mounted() {
    this.handleViewportChange = () => {
      this.isMobileViewport = window.matchMedia('(max-width: 980px)').matches
      if (!this.isMobileViewport) this.showNav = false
    }
    this.handleViewportChange()
    window.addEventListener('resize', this.handleViewportChange)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportChange)
  },

  methods: {
    normalizeSection(value) {
      if (value === 'tickets' || value === 'swap' || !value) return 'entries'
      return ['entries', 'sell', 'profile', 'support'].includes(value) ? value : 'entries'
    },
    normalizeSwapSection(value) {
      if (value === 'deliveries' || value === 'messages') return 'tickets'
      return ['sell', 'tickets', 'finance', 'profile'].includes(value) ? value : 'sell'
    },
    searchEvents(results, term) {
      this.search = results
      this.searchField = term
      this.main = term === ''
    },
    setSection(section) {
      this.activeSection = section
      this.$router.replace({ name: 'usertickets', query: { ...this.$route.query, tab: section } }).catch(() => {})
    },
    resetAccount() {
      this.orders = []
      this.swapOrders = []
      this.ticketCount = 0
      this.accountError = ''
      this.currentSession = null
      this.expandedEventKey = ''
      this.expandedSwapOrderNumber = ''
      this.swapOrderDetails = {}
      this.swapArtifacts = {}
      this.swapMessages = {}
    },
    loadAccount(session) {
      this.currentSession = session
      this.loadingAccount = true
      this.accountError = ''
      apiServiceAuth('/api/my/tickets/', 'GET', undefined, undefined, 'ticket').then((tickets) => {
        this.orders = tickets && Array.isArray(tickets.orders) ? tickets.orders : []
        this.swapOrders = tickets && Array.isArray(tickets.swap_orders) ? tickets.swap_orders : []
        this.ticketCount = tickets && tickets.ticket_count ? tickets.ticket_count : 0
      }).catch(() => {
        this.accountError = 'No pudimos cargar tu cuenta en este momento.'
      }).finally(() => {
        this.loadingAccount = false
      })
    },
    groupOrdersByEvent(orders) {
      const groups = new Map()
      ;(orders || []).forEach((order) => {
        const key = order.event_slug || order.event_name || `order-${order.invoice_id}`
        const existing = groups.get(key) || {
          key,
          event_name: order.event_name,
          event_slug: order.event_slug,
          event_date: order.event_date,
          event_banner_url: order.event_banner_url || '',
          event_is_fomo_original: Boolean(order.event_is_fomo_original),
          event_commerce_mode: order.event_commerce_mode || '',
          venue_name: order.venue_name,
          city_name: order.city_name,
          ticket_count: 0,
          total: 0,
          currency: order.currency || 'USD',
          badges: [],
          orders: [],
        }
        existing.ticket_count += this.itemCount(order)
        existing.total += Number.parseFloat(order.total || 0) || 0
        existing.event_banner_url = existing.event_banner_url || order.event_banner_url || ''
        existing.event_is_fomo_original = existing.event_is_fomo_original || Boolean(order.event_is_fomo_original)
        existing.event_commerce_mode = existing.event_commerce_mode || order.event_commerce_mode || ''
        ;(order.source_badges || [order.source === 'swap' ? 'Swap' : 'Original FOMO']).forEach((badge) => {
          if (badge && !existing.badges.includes(badge)) existing.badges.push(badge)
        })
        existing.orders.push(order)
        existing.status_label = this.groupStatusLabel(existing)
        groups.set(key, existing)
      })
      return Array.from(groups.values()).sort((a, b) => {
        const aPast = this.isPastDate(a.event_date)
        const bPast = this.isPastDate(b.event_date)
        if (aPast !== bPast) return aPast ? 1 : -1
        return new Date(a.event_date || 0) - new Date(b.event_date || 0)
      })
    },
    groupStatusLabel(group) {
      const orders = group && Array.isArray(group.orders) ? group.orders : []
      if (orders.some((order) => order.source === 'swap' && order.payment_status === 'verified')) return 'Pago confirmado'
      if (orders.some((order) => order.source === 'swap')) return 'Swap en proceso'
      if (orders.some((order) => order.ticket_ready)) return 'Archivo listo'
      return 'Emisión en curso'
    },
    toggleEventGroup(group) {
      this.expandedEventKey = this.expandedEventKey === group.key ? '' : group.key
      this.expandedSwapOrderNumber = ''
    },
    toggleSwapOrder(order) {
      this.expandedSwapOrderNumber = this.expandedSwapOrderNumber === order.order_number ? '' : order.order_number
      if (this.expandedSwapOrderNumber) this.refreshSwapOrder(order)
    },
    activeSwapOrder(order) {
      return this.swapOrderDetails[order.order_number] || order
    },
    refreshSwapOrder(order) {
      if (!order || !order.order_number || this.loadingSwapKey) return
      this.loadingSwapKey = order.order_number
      apiServiceAuth(`/api/swap/orders/${encodeURIComponent(order.order_number)}/`, 'GET', undefined, undefined, 'ticket').then((data) => {
        if (data && data.order_number && !data.err) {
          const normalized = this.normalizeSwapDetail(data, order)
          this.swapOrderDetails = { ...this.swapOrderDetails, [order.order_number]: normalized }
          this.swapOrders = this.swapOrders.map((item) => item.order_number === order.order_number ? { ...item, ...normalized } : item)
        }
      }).finally(() => {
        this.loadingSwapKey = ''
      })
    },
    updateSwapReceipt(data) { this.swapOrderDetails = { ...this.swapOrderDetails, [data.order_number]: this.normalizeSwapDetail(data) } },
    normalizeSwapDetail(data, fallback = {}) {
      const totals = data.totals || {}
      return {
        ...fallback,
        source: 'swap',
        raw: data,
        order_id: data.id || fallback.order_id,
        order_number: data.order_number,
        status: data.status,
        payment_status: data.payment_status,
        payout_status: data.payout_status,
        fomo_review_status: data.fomo_review_status,
        total: totals.customer_total || fallback.total || '0.00',
        subtotal: totals.ticket_subtotal || fallback.subtotal || '0.00',
        buyer_fee_total: totals.buyer_fee_total || fallback.buyer_fee_total || '0.00',
        currency: totals.currency || fallback.currency || 'USD',
        ticket_count: Number(totals.item_count || fallback.ticket_count || 0),
        event_name: data.event_name || fallback.event_name,
        event_slug: data.event_slug || fallback.event_slug,
        items: Array.isArray(data.items) ? data.items.map((item) => ({
          id: item.id,
          name: (item.snapshot && (item.snapshot.section_label || item.snapshot.zone_label)) || `Ticket #${item.id}`,
          quantity: 1,
          seat_name: item.snapshot ? [item.snapshot.section_label, item.snapshot.row_label, item.snapshot.seat_label].filter(Boolean).join(' · ') : '',
          delivery_status: item.delivery_status,
          snapshot: item.snapshot || {},
        })) : fallback.items || [],
        deliveries: Array.isArray(data.deliveries) ? data.deliveries : fallback.deliveries || [],
        delivery_summary: this.swapDeliverySummary(data.deliveries || fallback.deliveries),
      }
    },
    loadSwapArtifacts(order) {
      const email = this.accountEmail(order)
      if (!email || this.loadingArtifactsKey) return
      this.loadingArtifactsKey = order.order_number
      apiServiceAuth(`/api/swap/orders/${encodeURIComponent(order.order_number)}/delivery-artifacts/`, 'POST', { customer_email: email }, undefined, 'ticket').then((data) => {
        this.swapArtifacts = { ...this.swapArtifacts, [order.order_number]: data && Array.isArray(data.artifacts) ? data.artifacts : [] }
      }).finally(() => {
        this.loadingArtifactsKey = ''
      })
    },
    loadSwapMessages(order) {
      const email = this.accountEmail(order)
      if (!email || this.loadingMessagesKey) return
      this.loadingMessagesKey = order.order_number
      apiServiceAuth(`/api/swap/orders/${encodeURIComponent(order.order_number)}/messages/?customer_email=${encodeURIComponent(email)}`, 'GET', undefined, undefined, 'ticket').then((data) => {
        this.swapMessages = { ...this.swapMessages, [order.order_number]: data && Array.isArray(data.conversations) ? data.conversations : [] }
      }).finally(() => {
        this.loadingMessagesKey = ''
      })
    },
    accountEmail(order) {
      return (order && order.customer_email) || (this.currentSession && this.currentSession.email) || ''
    },
    isPastDate(value) {
      if (!value) return false
      return new Date(value).getTime() < Date.now()
    },
    formatMoney(value, currency = 'USD') {
      const amount = Number.parseFloat(value) || 0
      try { return new Intl.NumberFormat('es-EC', { style: 'currency', currency: currency || 'USD' }).format(amount) } catch (_error) { return `$${amount.toFixed(2)}` }
    },
    formatDate(value) {
      if (!value) return 'Fecha por confirmar'
      return new Intl.DateTimeFormat('default', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    },
    formatDateShort(value) {
      if (!value) return 'Fecha por confirmar'
      return new Intl.DateTimeFormat('default', { dateStyle: 'medium' }).format(new Date(value))
    },
    itemCount(order) {
      if (order && order.ticket_count) return Number(order.ticket_count || 0)
      return Array.isArray(order && order.items) ? order.items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0
    },
    orderKey(order) {
      return `${order.source || 'tickets'}-${order.invoice_id || order.order_number}`
    },
    orderStatusLabel(order) {
      if (!order) return 'Pendiente'
      if (order.source === 'swap') return `Pago ${this.paymentStatusLabel(order.payment_status)}`
      return order.ticket_ready ? 'archivo listo' : 'emisión en curso'
    },
    sourceBadgeClass(badge) {
      if (badge === 'Swap') return 'is-swap'
      return 'is-original'
    },
    eventBannerStyle(group) {
      if (group && group.event_banner_url) {
        return { backgroundImage: `linear-gradient(90deg, rgba(4,5,9,0.74), rgba(4,5,9,0.24)), url("${group.event_banner_url}")` }
      }
      return { backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.14), transparent 32%), linear-gradient(135deg, rgba(33,32,45,0.96), rgba(11,11,18,0.98))' }
    },
    paymentStatusLabel(status) {
      const labels = { pending: 'pendiente', unverified: 'por verificar', verified: 'confirmado', failed: 'fallido', refunded: 'reembolsado', chargeback: 'con reclamo' }
      return labels[status] || this.humanizeStatus(status).toLowerCase()
    },
    deliveryStatusLabel(status) {
      const labels = { pending_payment_verification: 'Pago por verificar', pending_seller_action: 'Por entregar', buyer_accessible: 'Lista para ver', delivered: 'Entregada', cancelled: 'Cancelada', refunded: 'Reembolsada' }
      return labels[status] || this.humanizeStatus(status)
    },
    swapDeliverySummary(deliveries) {
      if (!Array.isArray(deliveries) || !deliveries.length) return 'pendiente'
      const counts = deliveries.reduce((acc, delivery) => {
        const status = delivery.status || 'pending'
        acc[status] = (acc[status] || 0) + 1
        return acc
      }, {})
      return Object.keys(counts).map((status) => `${this.deliveryStatusLabel(status)} ${counts[status]}`).join(' · ')
    },
    humanizeStatus(value) {
      if (!value) return 'Pendiente'
      return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
  },
}
</script>

<style scoped>
.client-account-section {
  padding: 0 0 72px;
}

.client-account-workspace {
  position: relative;
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at top left, rgba(176, 185, 204, 0.08), transparent 24%),
    radial-gradient(circle at 82% 12%, rgba(76, 60, 94, 0.14), transparent 22%),
    linear-gradient(180deg, #06060b, #0a0910 42%, #0d0b14 100%);
}

.client-account-shell {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(236px, 280px) minmax(0, 1fr);
  align-items: flex-start;
  gap: 20px;
  max-width: 1480px;
  margin: 0 auto;
}

.workspace-v2__nav--mobile {
  display: none;
}

.workspace-v2__nav--desktop {
  display: block;
  position: relative;
  z-index: 5;
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  min-width: 0;
  align-self: flex-start;
}

.client-account-main {
  position: relative;
  z-index: 1;
  grid-column: 2;
  grid-row: 1;
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  gap: 18px;
}

.client-account-stack {
  width: min(1180px, calc(100% - 28px));
  margin: 0 auto;
}

.client-account-metrics {
  margin-top: 18px;
}

.client-account-menu {
  position: sticky;
  top: 74px;
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  backdrop-filter: blur(18px);
}

.client-account-menu .workspace-segmented-control__item {
  justify-content: center;
  gap: 8px;
}

.client-account-metric--email strong {
  display: block;
  max-width: 100%;
  font-size: clamp(0.98rem, 1.45vw, 1.28rem);
  letter-spacing: -0.04em;
  line-height: 1.14;
  overflow-wrap: break-word;
  word-break: normal;
  white-space: normal;
}

.client-account-link {
  text-decoration: none;
}

.client-account-empty-state {
  display: grid;
  justify-items: start;
  gap: 10px;
}

.client-account-empty-state strong {
  color: #fff;
  font-size: 1.04rem;
}

.client-account-empty-state span {
  color: rgba(194, 204, 223, 0.76);
}

.client-entry-card__banner {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  min-height: 210px;
  padding: 18px;
  border-radius: 24px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.client-entry-card__banner::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.62));
}

.client-entry-card__banner > * {
  position: relative;
  z-index: 1;
}

.client-entry-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.purchase-ticket-card__status.is-original {
  background: rgba(214, 221, 232, 0.18);
  color: #f8fbff;
}

.purchase-ticket-card__status.is-swap {
  background: rgba(120, 240, 202, 0.18);
  color: #d8fff0;
}

.client-entry-card__details {
  padding-top: 4px;
}

@media (max-width: 980px) {
  .client-account-workspace {
    padding: 12px;
  }

  .workspace-v2__nav--mobile {
    display: block;
  }

  .workspace-v2__nav--desktop {
    display: none;
  }

  .client-account-shell {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .client-account-main {
    gap: 14px;
  }

  .client-account-metrics {
    gap: 10px;
  }

  .client-account-metric--email strong {
    font-size: 0.82rem;
    letter-spacing: -0.03em;
  }
}

.purchase-ticket-card--swap {
  border-color: rgba(120, 240, 202, 0.18);
}

.purchase-ticket-list--compact .purchase-ticket-card,
.purchase-ticket-card.is-past {
  opacity: 0.86;
}

.client-account-swap-detail {
  display: grid;
  gap: 14px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(201, 211, 231, 0.1);
}

.client-account-artifacts,
.client-account-messages {
  display: grid;
  gap: 10px;
}

.client-account-artifacts article,
.client-account-messages article {
  display: grid;
  gap: 6px;
  padding: 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(201, 211, 231, 0.1);
}

.client-account-artifacts strong,
.client-account-messages strong,
.client-account-artifacts a {
  color: #fff;
}

.client-account-artifacts span,
.client-account-messages p {
  margin: 0;
  color: rgba(194, 204, 223, 0.8);
}

.ticket-vault-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 18px;
}

.ticket-vault-card {
  display: grid;
  gap: 16px;
  padding: 20px;
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.24);
}

.ticket-vault-card__copy {
  display: grid;
  gap: 8px;
}

.ticket-vault-card__preview {
  display: grid;
  justify-items: start;
  gap: 10px;
}

.ticket-vault-card__preview-frame {
  padding: 10px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(201, 211, 231, 0.08);
}

.ticket-vault-card--spotlight {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
}

.ticket-vault-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ticket-vault-card__kicker,
.purchase-ticket-card__eyebrow,
.purchase-ticket-pass__kicker {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.ticket-vault-card h3,
.purchase-ticket-card__top h3,
.purchase-ticket-pass__frame strong,
.purchase-ticket-card__amount strong {
  margin: 0;
  color: #fff;
}

.ticket-vault-card p,
.purchase-ticket-card__top p,
.purchase-ticket-card__item small,
.purchase-ticket-card__item span,
.purchase-ticket-card__amount small,
.purchase-ticket-card__pending,
.purchase-ticket-pass__frame small,
.purchase-ticket-pass__meta span {
  margin: 0;
  color: rgba(194, 204, 223, 0.8);
}

.ticket-vault-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ticket-vault-card__stats span,
.purchase-ticket-card__status {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #dbe4f3;
  font-size: 0.72rem;
  font-weight: 700;
}

.purchase-ticket-card__status.is-ready {
  background: rgba(94, 151, 123, 0.18);
}

.purchase-ticket-list {
  display: grid;
  gap: 16px;
}

.purchase-ticket-card {
  display: grid;
  gap: 16px;
  padding: 20px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.07), transparent 26%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.16), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.95);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow: 0 24px 54px rgba(0, 0, 0, 0.24);
}

.purchase-ticket-card__topline {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.purchase-ticket-card__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 18px;
}

.purchase-ticket-card__content {
  display: grid;
  gap: 16px;
}

.purchase-ticket-card__hero {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}

.purchase-ticket-card__hero-meta {
  display: grid;
  gap: 4px;
  justify-items: end;
  text-align: right;
}

.purchase-ticket-card__details-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.purchase-ticket-card__detail {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(201, 211, 231, 0.06);
}

.purchase-ticket-card__detail span {
  color: rgba(194, 204, 223, 0.72);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.purchase-ticket-card__detail strong {
  color: #fff;
  font-size: 0.96rem;
  line-height: 1.35;
}

.purchase-ticket-card__top,
.purchase-ticket-card__item,
.purchase-ticket-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.purchase-ticket-card__top h3,
.purchase-ticket-card__top p,
.purchase-ticket-card__item strong,
.purchase-ticket-card__item small,
.purchase-ticket-card__item span,
.purchase-ticket-card__pending {
  margin: 0;
}

.purchase-ticket-pass {
  display: grid;
}

.purchase-ticket-pass__surface {
  position: relative;
  display: grid;
  gap: 12px;
  height: 100%;
  padding: 18px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 1px rgba(255, 255, 255, 0.02);
}

.purchase-ticket-pass__surface::before,
.purchase-ticket-pass__surface::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #09090f;
  border: 1px solid rgba(201, 211, 231, 0.08);
  transform: translateY(-50%);
}

.purchase-ticket-pass__surface::before {
  left: -9px;
}

.purchase-ticket-pass__surface::after {
  right: -9px;
}

.purchase-ticket-pass__top,
.purchase-ticket-pass__footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.purchase-ticket-pass__code,
.purchase-ticket-pass__subline,
.purchase-ticket-pass__footer span {
  color: rgba(194, 204, 223, 0.76);
  font-size: 0.78rem;
}

.purchase-ticket-pass__event {
  font-size: 1.08rem;
  line-height: 1.18;
}

.purchase-ticket-pass__subline {
  margin-top: -4px;
}

.purchase-ticket-pass__qr-shell {
  display: grid;
  justify-content: center;
  padding: 12px 0 8px;
}

.purchase-ticket-card__items {
  display: grid;
  gap: 10px;
}

.purchase-ticket-card__item {
  align-items: center;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(201, 211, 231, 0.05);
}

.purchase-ticket-card__item-main,
.purchase-ticket-card__item-side {
  display: grid;
  gap: 4px;
}

.purchase-ticket-card__item-side {
  justify-items: end;
  text-align: right;
}

@media (max-width: 720px) {
  .client-account-workspace {
    padding: 12px;
  }

  .workspace-v2__nav--mobile {
    display: block;
  }

  .workspace-v2__nav--desktop {
    display: none;
  }

  .client-account-shell {
    display: grid;
    grid-template-columns: 1fr;
  }

  .client-entry-card__banner {
    min-height: 168px;
    display: grid;
    align-content: end;
  }

  .client-account-menu {
    position: relative;
    top: auto;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    overflow: visible;
    padding: 9px;
    border-radius: 28px;
  }

  .client-account-menu .workspace-segmented-control__item {
    min-width: 0;
    min-height: 46px;
    padding: 0 12px;
  }

  .client-account-menu .workspace-segmented-control__item:last-child {
    grid-column: 1 / -1;
  }

  .ticket-vault-grid,
  .purchase-ticket-card__layout {
    grid-template-columns: 1fr;
  }

  .ticket-vault-card--spotlight,
  .purchase-ticket-card__hero,
  .purchase-ticket-pass__top,
  .purchase-ticket-pass__footer {
    display: grid;
  }

  .purchase-ticket-card__details-grid {
    grid-template-columns: 1fr;
  }

  .purchase-ticket-card__topline,
  .purchase-ticket-card__top,
  .purchase-ticket-card__item,
  .purchase-ticket-card__actions {
    display: grid;
  }

  .purchase-ticket-card__item-side,
  .purchase-ticket-card__hero-meta {
    justify-items: start;
    text-align: left;
  }
}
</style>
